import {
  IHicapsConnectBridgeEdge,
  IHicapsConnectEdge,
  IHicapsConnectTerminalV2,
} from '@principle-theorem/principle-core/interfaces';
import { Timestamp } from '@principle-theorem/shared';
import { isEmpty, values } from 'lodash';

export enum HicapsConnectEdgeStatus {
  Active = 'active',
  Stale = 'stale',
  Disabled = 'disabled',
}

export interface IHicapsServerUrlParseResult {
  terminalId: string;
  isLocal: boolean;
  serverUrl: string;
}

// Firestore rejects these in document ids and map keys; a malformed HICAPS
// identifier must be skipped rather than corrupt the terminal document.
const invalidKeyCharacters = /[./[\]*]/;

// HICAPS reports the terminal's attachment in the last field of a server URL
// (`<host>:<port>:<terminalId>:<attachment>`). `COMT` means the terminal is
// reached over the network; any numbered port (`COM4`, `COM5`) is a serial
// cable owned by the bridge that reported it.
const networkComPort = 'COMT';

/**
 * Owns what a V2 HICAPS terminal is: how the identifiers HICAPS reports are
 * read, and what its bridge and merchant edges say about whether it can be
 * used. Every consumer — sync, routing, payment-time selection and the practice
 * settings screens — asks these questions, so the answers live in one place
 * rather than being re-derived per surface.
 */
export class HicapsConnectTerminal {
  /**
   * Collapses the two independent flags an edge carries into the single state a
   * reader cares about, with user intent (`disabled`) outranking an observation
   * the last sync happened to miss (`stale`).
   */
  static edgeStatus(edge: IHicapsConnectEdge<object>): HicapsConnectEdgeStatus {
    if (edge.disabled) {
      return HicapsConnectEdgeStatus.Disabled;
    }
    if (edge.stale) {
      return HicapsConnectEdgeStatus.Stale;
    }
    return HicapsConnectEdgeStatus.Active;
  }

  /**
   * A bridge edge answers "can this bridge still reach the terminal", so a stale
   * edge must not be routed to.
   */
  static isBridgeEdgeActive(edge: IHicapsConnectBridgeEdge): boolean {
    return (
      HicapsConnectTerminal.edgeStatus(edge) === HicapsConnectEdgeStatus.Active
    );
  }

  /**
   * "Last active" is a property of the terminal, not of any one bridge, so it
   * takes the most recent sighting across every bridge that has ever reported
   * it — including stale and disabled ones, which still witnessed the terminal.
   */
  static lastActive(
    terminal: Pick<IHicapsConnectTerminalV2, 'bridges'>
  ): Timestamp | undefined {
    const lastSeenTimes = values(terminal.bridges).map((edge) => edge.lastSeen);
    const [firstLastSeen, ...remainingLastSeen] = lastSeenTimes;

    if (!firstLastSeen) {
      return;
    }

    return remainingLastSeen.reduce(
      (latest, lastSeen) =>
        lastSeen.toMillis() > latest.toMillis() ? lastSeen : latest,
      firstLastSeen
    );
  }

  /**
   * A terminal has no disabled flag of its own — it is disabled when the user
   * has disabled every merchant on it, because nothing is then left to offer.
   */
  static isDisabled(
    terminal: Pick<IHicapsConnectTerminalV2, 'merchants'>
  ): boolean {
    // An empty map must not read as disabled: `[].every()` is true, which would
    // hide a terminal a bridge can see but whose merchant list came back empty.
    if (isEmpty(terminal.merchants)) {
      return false;
    }
    return values(terminal.merchants).every((edge) => !!edge.disabled);
  }

  /**
   * HICAPS enumerates terminals as server URLs, but a terminal's identity is the
   * bare terminal id inside one — the same physical terminal reached over the
   * network and over a serial cable must resolve to a single document.
   */
  static parseServerUrl(serverUrl: string): IHicapsServerUrlParseResult {
    const [, , terminalId, comPort] = serverUrl.split(':');

    return {
      terminalId: terminalId ?? serverUrl,
      isLocal: !!comPort && comPort !== networkComPort,
      serverUrl,
    };
  }

  /**
   * Terminal and merchant ids reported by HICAPS become Firestore document ids
   * and map keys verbatim, so one that Firestore cannot store has to be
   * rejected before it corrupts a write rather than after.
   */
  static isValidKey(key: string): boolean {
    return key.length > 0 && !invalidKeyCharacters.test(key);
  }
}
